import React, { useState, useRef, useEffect, useCallback, useMemo, FC, ReactNode, MouseEvent } from 'react';
import { DropdownProps, DropdownItemProps, DropdownHeaderProps } from '../../types/components.d';

const Dropdown: FC<DropdownProps> = ({ 
  trigger, 
  children, 
  position = 'bottom-left',
  className = '',
  onOpen,
  onClose,
  closeOnClickOutside = true,
  closeOnSelect = true
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left?: number; right?: number } | null>(null);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node;
    const clickedInsideTrigger = dropdownRef.current?.contains(target);
    const clickedInsideMenu = menuRef.current?.contains(target);
    if (!clickedInsideTrigger && !clickedInsideMenu) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (closeOnClickOutside) {
      document.addEventListener('mousedown', handleClickOutside as any);
      return () => document.removeEventListener('mousedown', handleClickOutside as any);
    }
  }, [closeOnClickOutside, handleClickOutside]);

  useEffect(() => {
    if (isOpen) {
      onOpen && onOpen();
    } else {
      onClose && onClose();
    }
  }, [isOpen, onOpen, onClose]);

  const positionClasses = useMemo(() => ({
    'bottom-left': '',
    'bottom-right': '',
    'top-left': '',
    'top-right': '',
    'left-top': '',
    'left-bottom': '',
    'right-top': '',
    'right-bottom': '',
  }), []);

  const handleTriggerClick = useCallback(() => {
    const node = triggerRef.current;
    if (node) {
      const rect = node.getBoundingClientRect();
      const top = rect.bottom + window.scrollY + 4; // small gap
      if (position.includes('right')) {
        const right = window.innerWidth - rect.right - 4;
        setCoords({ top, right });
      } else {
        setCoords({ top, left: rect.left + window.scrollX });
      }
    }
    setIsOpen(!isOpen);
  }, [position]);

  const handleItemClick = useCallback(() => {
    if (closeOnSelect) {
      setIsOpen(false);
    }
  }, [closeOnSelect]);

  return (
    <div className={`relative inline-block ${className}`} ref={dropdownRef}>
      {/* Trigger */}
      <div onClick={handleTriggerClick} className="cursor-pointer" ref={triggerRef}>
        {trigger}
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={`fixed z-[1000] w-56 bg-white rounded-xl shadow-large border border-gray-100 py-2 animate-slide-down overflow-hidden`}
          style={{ top: coords?.top, ...(coords?.right !== undefined ? { right: coords.right } : { left: coords?.left }) }}
          ref={menuRef}
        >
          <div onClick={handleItemClick}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

// Dropdown Item Component
export const DropdownItem: FC<DropdownItemProps> = ({ 
  children, 
  onClick, 
  icon, 
  danger = false,
  disabled = false,
  className = '' 
}) => {
  const baseClasses = 'flex items-center w-full px-4 py-3 text-sm font-medium transition-colors duration-200';
  const stateClasses = disabled 
    ? 'text-gray-400 cursor-not-allowed' 
    : danger 
    ? 'text-red-600 hover:bg-red-50 cursor-pointer' 
    : 'text-gray-700 hover:bg-primary-teaGreen hover:text-primary-black cursor-pointer';

  return (
    <div
      className={`${baseClasses} ${stateClasses} ${className}`}
      onClick={disabled ? undefined : onClick}
    >
      {icon && (
        <span className="mr-3 text-gray-400">
          {icon}
        </span>
      )}
      {children}
    </div>
  );
};

// Dropdown Divider Component
export const DropdownDivider: FC = () => (
  <div className="my-2 border-t border-gray-100"></div>
);

// Dropdown Header Component
export const DropdownHeader: FC<DropdownHeaderProps> = ({ children, className = '' }) => (
  <div className={`px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider ${className}`}>
    {children}
  </div>
);

export default Dropdown;
